import * as React from 'react';
import { useEffect, useState } from 'react';
import { API, Storage } from 'aws-amplify';
import Dropzone from 'react-dropzone';
import { X, Loader2, ImageOff, Upload } from 'lucide-react';
import { updateLessonDetail } from '../../../../graphql/mutations';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { FormField } from '../../../components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { usePermissions } from '../../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../../application/context/PermissionsContext/permissionTypes';

declare const global: {
  me: { orgID: string };
  trainingVideos: Record<string, any[]> | null;
};

interface Lesson {
  id: string;
  name: string;
  description: string;
  youTubeURL: string | null;
  url: string;
  urlType: string;
  order: number;
  imageName: string;
  imageType: string;
  posterImageName: string | null;
  category: string;
  createdAt: string;
  videoURL?: string;
}

interface EditLessonModalProps {
  open: boolean;
  onClose: () => void;
  lesson: Lesson;
  refresh: () => void;
  snackbar: (message: string, severity: string) => void;
}

export function EditLessonModal({
  open,
  onClose,
  lesson,
  refresh,
  snackbar,
}: EditLessonModalProps) {
  const { checkPermission } = usePermissions();
  const canManage = checkPermission(PermissionKeys.Other_ManageTrainingVideos);

  const [ogLesson] = useState<Lesson>(lesson);
  const [lessonDetails, setLessonDetails] = useState<Lesson>(lesson);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDisplay, setImageDisplay] = useState<string | null>(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; category?: string }>(
    {}
  );
  const [submitted, setSubmitted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [createNewCategory, setCreateNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  const hasChanges =
    JSON.stringify(ogLesson) !== JSON.stringify(lessonDetails) ||
    imageFile !== null;

  useEffect(() => {
    const newErrors: typeof errors = {};
    if (!lessonDetails.name || !lessonDetails.name.length) {
      newErrors.name = 'Title is required';
    }
    if (!lessonDetails.category || !lessonDetails.category.length) {
      newErrors.category = 'Category is required';
    }
    setErrors(newErrors);
  }, [lessonDetails]);

  const handleThumbnailDrop = (droppedFiles: File[]) => {
    const formats = ['jpeg', 'jpg', 'png', 'gif'];
    const image = droppedFiles[0];
    const ext = image.name.split('.').pop()?.toLowerCase() || '';
    if (!formats.includes(ext)) {
      snackbar('Thumbnail must be a PNG, GIF, or JPEG image', 'error');
      return;
    }
    setImageFile(image);
    setImageDisplay(URL.createObjectURL(image));
    setLessonDetails((prev) => ({
      ...prev,
      posterImageName: image.name.replace(/^.*[\\/]/, ''),
    }));
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length > 0) {
      return;
    }
    setSavingChanges(true);

    const submitObj: any = {
      id: lessonDetails.id,
      category: newCategory.length ? newCategory : lessonDetails.category,
      description: lessonDetails.description,
      name: lessonDetails.name,
      order: lessonDetails.order,
    };

    if (
      lessonDetails.posterImageName !== ogLesson.posterImageName &&
      imageFile
    ) {
      if (!lessonDetails.posterImageName?.startsWith('https://')) {
        submitObj.posterImageName = `${
          global.me.orgID
        }_${lessonDetails.posterImageName?.replace(/^.*[\\/]/, '')}`;
        await Storage.put(submitObj.posterImageName, imageFile, {
          contentType: imageFile.type,
        });
      } else {
        submitObj.posterImageName = lessonDetails.posterImageName;
      }
    }

    try {
      await API.graphql({
        query: updateLessonDetail,
        variables: { input: submitObj },
      });
      snackbar(`Updated Video Information - ${submitObj.name}`, 'info');
      refresh();
      onClose();
    } catch (err) {
      console.error('Error updating lesson:', err);
      snackbar('Failed to update video', 'error');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDelete = async () => {
    try {
      await API.graphql({
        query: updateLessonDetail,
        variables: { input: { id: lessonDetails.id, isDeleted: true } },
      });
      snackbar(`Video Deleted - ${lessonDetails.name}`, 'error');
      refresh();
      onClose();
    } catch (err) {
      console.error('Error deleting lesson:', err);
      snackbar('Failed to delete video', 'error');
    }
  };

  if (!canManage || !open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-background rounded-xl p-6 max-w-sm w-full mx-4 space-y-4 text-center">
            <p className="font-semibold text-lg text-foreground">
              Are you sure you want to delete this training video?
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="destructive" onClick={handleDelete}>
                Yes, Delete
              </Button>
              <Button variant="outline" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main modal */}
      <div className="bg-background rounded-xl max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto shadow-xl relative">
        {/* Header */}
        <div className="sticky top-0 bg-muted px-5 py-3 rounded-t-xl flex items-center justify-between">
          <p className="font-semibold text-foreground">
            Edit - {ogLesson.name}
          </p>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-background/50 transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Thumbnail preview */}
        <div className="flex flex-col items-center p-5 gap-3">
          {imageDisplay || lessonDetails.posterImageName ? (
            <img
              src={imageDisplay || lessonDetails.posterImageName || ''}
              alt="Thumbnail"
              className="w-full max-w-[300px] aspect-video rounded-lg object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '';
              }}
            />
          ) : (
            <div className="w-full max-w-[300px] aspect-video rounded-lg bg-gradient-to-tr from-white to-primary flex flex-col items-center justify-center">
              <ImageOff className="size-10 text-white" />
              <p className="text-white font-semibold text-sm mt-1">
                No Thumbnail
              </p>
            </div>
          )}
          <Dropzone onDrop={handleThumbnailDrop} disabled={savingChanges}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={savingChanges}
                  className="gap-2"
                >
                  <Upload className="size-3.5" />
                  Change Thumbnail
                </Button>
              </div>
            )}
          </Dropzone>
        </div>

        {/* Form fields */}
        <div className="px-5 pb-5 space-y-4">
          <FormField label="Title" error={submitted ? errors.name : undefined}>
            <Input
              disabled={savingChanges}
              value={lessonDetails.name}
              onChange={(e) =>
                setLessonDetails((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              placeholder="Training media title..."
            />
          </FormField>

          <FormField label="Description">
            <Textarea
              disabled={savingChanges}
              rows={3}
              value={lessonDetails.description || ''}
              onChange={(e) =>
                setLessonDetails((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Training media description..."
            />
          </FormField>

          <div className="flex gap-3">
            <div className="flex-1">
              <FormField
                label="Category"
                error={submitted ? errors.category : undefined}
              >
                {createNewCategory ? (
                  <div className="flex gap-2">
                    <Input
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="New category name..."
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      disabled={!newCategory.length}
                      onClick={() => {
                        setLessonDetails((prev) => ({
                          ...prev,
                          category: newCategory,
                        }));
                        setCreateNewCategory(false);
                      }}
                    >
                      Create
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateNewCategory(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Select
                    disabled={savingChanges}
                    value={lessonDetails.category}
                    onValueChange={(val) => {
                      if (val === '@add-new-category') {
                        setCreateNewCategory(true);
                      } else {
                        setLessonDetails((prev) => ({
                          ...prev,
                          category: val,
                        }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {global.trainingVideos &&
                        Object.keys(global.trainingVideos).map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      {newCategory.length > 0 && (
                        <SelectItem value={newCategory}>
                          {newCategory}
                        </SelectItem>
                      )}
                      <SelectItem value="@add-new-category">
                        + Add New Category
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </FormField>
            </div>
            <div className="w-20">
              <FormField label="Order">
                <Input
                  type="number"
                  disabled={savingChanges}
                  value={lessonDetails.order}
                  onChange={(e) =>
                    setLessonDetails((prev) => ({
                      ...prev,
                      order: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </FormField>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-background border-t border-border px-5 py-3 flex justify-between rounded-b-xl">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || savingChanges}
            className="gap-2"
          >
            {savingChanges && <Loader2 className="size-4 animate-spin" />}
            Save Changes
          </Button>
          <Button
            variant="destructive"
            onClick={() => setConfirmDelete(true)}
            disabled={savingChanges}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
